import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import CustomIcon from '../Elements/CustomIcon';
import Label from '../Elements/Label';
import { ELECTION_TYPE } from '../../../../utils/constants';
import Select from '../Elements/Select';

import CountrySelector from '../Elements/CountrySelector';
import TextArea from '../Elements/TextArea';
import Span from '../Elements/Span';
import Markdown from '../Elements/Markdown';
import { Context } from '../Wizard/WizardWrapper';

import InputText from '../Elements/InputText';
import InputEmail from '../Elements/InputEmail';
import { stringUcFirst } from '../../../../utils/stringUcFirst';
import { useStaticCollections } from '../../../../context/StaticCollections';
import { useRefereeValidation } from '../../hooks/useRefereeValidation';

const NominationReferee = ({
  item,
  className,
  classNameMarginTop,
  fieldsValidated,
}) => {
  const { setValue, watch, resetField, errors, clearErrors, setError } =
    useContext(Context);

  const watchIsFellow = watch(item.name);
  const watchFellowId = watch(`${item.name}FellowId`);
  const watchNonAasRefereeId = watch(`nonAas${stringUcFirst(item.name)}Id`);

  const [incompleteNonAasReferee, setIncompleteNonAasReferee] = useState(false);
  const [selectedNonAasReferee, setSelectedNonAasReferee] = useState(null);
  const [selectedFellow, setSelectedFellow] = useState(null);
  const [showInactiveWarningNonAas, setShowInactiveWarningNonAas] =
    useState(false);
  const [showInactiveWarningFellow, setShowInactiveWarningFellow] =
    useState(false);

  const { fellowsArray, nonAasRefereesArray } = useStaticCollections();

  const firstName = watch(`${item.name}Name`);
  const lastName = watch(`${item.name}LastName`);
  const enteredEmail = watch(`${item.name}Email`);

  useRefereeValidation({
    fieldPrefix: item.name,
    firstName,
    lastName,
    email: enteredEmail,
    fellowsArray,
    nonAasRefereesArray,
    errors,
    setError,
    clearErrors,
  });

  useEffect(() => {
    const nonAasReferee = [
      'TitleId',
      'Name',
      'LastName',
      'Email',
      'CountryId',
      'Memberships',
    ];

    if (watchNonAasRefereeId && parseInt(watchNonAasRefereeId.value) === 0) {
      nonAasReferee.forEach((fieldName) => {
        const fieldPath = `${item.name}${fieldName}`;
        if (!watch(fieldPath)) {
          setError(fieldPath, {
            type: 'required',
            message: 'This field is required',
          });
        }
      });
    }

    if (
      (watchNonAasRefereeId && parseInt(watchIsFellow) === 1) ||
      (watchNonAasRefereeId && parseInt(watchNonAasRefereeId.value) > 0)
    ) {
      if (
        !watchFellowId ||
        (typeof watchFellowId === 'object' &&
          Object.keys(watchFellowId).length === 0)
      ) {
        setValue(`${item.name}FellowId`, '', { shouldValidate: true });
      }

      nonAasReferee.forEach((fieldName) => {
        if (watch(`${item.name}${fieldName}`)) {
          resetField(`${item.name}${fieldName}`);
        }
      });

      if (watch(`${item.name}EmailEducationalInstitution`)) {
        resetField(`${item.name}EmailEducationalInstitution`);
      }

      if (watch(`${item.name}Organisation`)) {
        resetField(`${item.name}Organisation`);
      }
    }

    if (parseInt(watchIsFellow) === 0) {
      resetField(`${item.name}FellowId`);
    }

    if (parseInt(watchIsFellow) === 1) {
      resetField(`nonAas${stringUcFirst(item.name)}Id`);
    }
  }, [
    item.name,
    setValue,
    watch,
    resetField,
    setError,
    watchIsFellow,
    watchFellowId,
    watchNonAasRefereeId,
  ]);

  useEffect(() => {
    if (watchFellowId && fellowsArray) {
      const fellow = fellowsArray.find(
        (f) => f.id === parseInt(watchFellowId.value),
      );

      if (fellow) {
        setSelectedFellow(fellow);
        const isInactive = fellow.attributes.active === false;
        setShowInactiveWarningFellow(isInactive);
      }
    } else {
      setShowInactiveWarningFellow(false);
      setSelectedFellow(null);
    }
  }, [watchFellowId, fellowsArray]);

  useEffect(() => {
    if (watchNonAasRefereeId) {
      if (
        nonAasRefereesArray &&
        watchNonAasRefereeId.value &&
        parseInt(watchNonAasRefereeId.value) > 0
      ) {
        const nonAasReferee = nonAasRefereesArray.find(
          (referee) => referee.id === parseInt(watchNonAasRefereeId.value),
        );

        setSelectedNonAasReferee(nonAasReferee);

        if (nonAasReferee && nonAasReferee.attributes) {
          const hasMemberships =
            nonAasReferee.attributes.memberships !== null &&
            nonAasReferee.attributes.memberships !== '';

          setIncompleteNonAasReferee(!hasMemberships);

          const isInactive = nonAasReferee.attributes.isActive === false;
          setShowInactiveWarningNonAas(isInactive);

          if (!hasMemberships) {
            if (!watch(`${item.name}SuppliedMemberships`)) {
              setValue(`${item.name}SuppliedMemberships`, '', {
                shouldValidate: true,
                shouldDirty: true,
              });
            }
          } else {
            if (watch(`${item.name}SuppliedMemberships`) !== undefined) {
              setValue(`${item.name}SuppliedMemberships`, undefined, {
                shouldValidate: false,
                shouldDirty: false,
              });
            }

            if (clearErrors) clearErrors(`${item.name}SuppliedMemberships`);
          }
        }
      } else {
        setIncompleteNonAasReferee(false);
        setSelectedNonAasReferee(null);
        setShowInactiveWarningNonAas(false);

        if (watch(`${item.name}SuppliedMemberships`) !== undefined) {
          setValue(`${item.name}SuppliedMemberships`, undefined, {
            shouldValidate: false,
            shouldDirty: false,
          });
          if (clearErrors) clearErrors(`${item.name}SuppliedMemberships`);
        }
      }
    }
  }, [
    watchNonAasRefereeId,
    nonAasRefereesArray,
    item.name,
    setValue,
    resetField,
    clearErrors,
    watch,
  ]);

  if (
    ['refereeFive', 'refereeSix'].includes(item.name) &&
    (parseInt(watch('electionTypeId')) === ELECTION_TYPE.CORRESPONDING_MEMBER ||
      parseInt(watch('electionTypeId')) === ELECTION_TYPE.SPECIAL_ELECTION)
  ) {
    if (watch(item.name) !== null && watch(item.name) !== undefined) {
      resetField(item.name);
      resetField(`${item.name}FellowId`);
    }

    return null;
  }

  const classNameMarginBottom = Number.isInteger(parseInt(watch(item.name)))
    ? ''
    : 'mb-lg-5';

  const nonAasRefereeKey = `nonAas${stringUcFirst(item.name)}Id`;

  return (
    <React.Fragment key={`referee-field-collection-${item.name}`}>
      <Markdown
        className='col-xl-12 col-lg-12 mt-xl-5 card-header'
        text={
          item.label !== null && item.label !== undefined
            ? `**Referee ${item.label.match(/\d+/g)[0]}**`
            : '**Referee**'
        }
      />

      <div
        className={`${className} ${classNameMarginTop} ${classNameMarginBottom}`}
      >
        <div className='form-group'>
          <Label
            className='mb-3'
            label={item.label}
            name={item.name}
            errors={errors}
            required={item.required}
          />

          <CustomIcon
            fieldsValidated={fieldsValidated}
            fieldError={errors[item.name]}
            required={item.required}
          />

          <Select options='binaries' name={item.name} />

          <Span text={errors[item.name] ? errors[item.name].message : ''} />
        </div>

        <Markdown
          className='col-xl-12 col-lg-12 mt-3'
          text={item.description}
        />
      </div>

      <div />

      {parseInt(watch(item.name)) === 0 ? (
        <div
          className={`${className} ${classNameMarginTop} ${watchNonAasRefereeId && watchNonAasRefereeId.value === 0 ? '' : 'mb-lg-5'}`}
        >
          <div className='form-group'>
            <Label
              className='mb-3'
              label='Select existing non AAS referee'
              name={nonAasRefereeKey}
              errors={errors}
              required={item.required}
            />

            <CustomIcon
              fieldsValidated={fieldsValidated}
              fieldError={errors[nonAasRefereeKey]}
              required={item.required}
            />

            <Select options='non-aas-referees' name={nonAasRefereeKey} />

            {errors[nonAasRefereeKey] ? (
              <Span text={errors[nonAasRefereeKey].message} />
            ) : (
              ''
            )}

            {showInactiveWarningNonAas &&
              selectedNonAasReferee &&
              parseInt(watchNonAasRefereeId?.value) > 0 && (
                <div className='alert alert-warning mt-3' role='alert'>
                  <i className='bi bi-exclamation-triangle-fill me-2' />
                  <strong>Warning:</strong>{' '}
                  {selectedNonAasReferee.attributes.name}{' '}
                  {selectedNonAasReferee.attributes.lastName} is inactive.
                </div>
              )}

            {!watchNonAasRefereeId ||
            !watchNonAasRefereeId.value ||
            watchNonAasRefereeId.value === '' ? (
              <div className='col-xl-12 col-lg-12 mt-3'>
                <p>
                  If not listed, scroll to the bottom to select -- My non AAS
                  referee is not listed --
                </p>
                <p>
                  Please contact{' '}
                  <a href='mailto:fellowship@science.org.au'>
                    fellowship@science.org.au
                  </a>{' '}
                  if a listed referee's email address has changed.
                </p>
              </div>
            ) : null}
          </div>

          <Markdown
            className='col-xl-12 col-lg-12 mt-3'
            text={item.description}
          />

          {incompleteNonAasReferee &&
          selectedNonAasReferee &&
          parseInt(watchNonAasRefereeId?.value) > 0 ? (
            <div className='form-group mt-lg-4'>
              <Label
                label="This referee doesn't have any Academy Memberships listed. Please provide them below (eg., FAA FTSE FASc FMRS)."
                name={`${item.name}SuppliedMemberships`}
                errors={errors}
                required
              />

              <CustomIcon
                fieldsValidated={fieldsValidated}
                fieldError={errors[`${item.name}SuppliedMemberships`]}
                required
              />

              <Markdown
                key='supplied-memberships-description'
                className='col-xl-12 col-lg-12'
              />

              <TextArea
                name={`${item.name}SuppliedMemberships`}
                className={className}
              />

              {errors[`${item.name}SuppliedMemberships`] ? (
                <Span
                  text={errors[`${item.name}SuppliedMemberships`].message}
                />
              ) : (
                ''
              )}
            </div>
          ) : null}
        </div>
      ) : (
        ''
      )}

      {parseInt(watch(`nonAas${stringUcFirst(item.name)}Id`)?.value) === 0 ? (
        <>
          <div className={`${className}  ${classNameMarginBottom}`}>
            <div className='form-group mt-2'>
              <Label
                className='mt-5'
                label='Title:'
                name={`${item.name}TitleId`}
                errors={errors}
                required={item.required}
              />

              <CustomIcon
                fieldsValidated={fieldsValidated}
                fieldError={errors[`${item.name}TitleId`]}
                required={item.required}
              />

              <Select options='titles' name={`${item.name}TitleId`} />

              {errors[`${item.name}TitleId`] ? (
                <Span text={errors[`${item.name}TitleId`].message} />
              ) : (
                ''
              )}
            </div>
          </div>
          <div className={`${className} ${classNameMarginTop}`}>
            <div className='form-group mt-2'>
              <Label
                label='Given Name'
                name={`${item.name}Name`}
                errors={errors}
                required={item.required}
              />

              <CustomIcon
                fieldsValidated={fieldsValidated}
                fieldError={errors[`${item.name}Name`]}
                required={item.required}
              />

              <InputText name={`${item.name}Name`} />

              {errors[`${item.name}Name`] ? (
                <Span text={errors[`${item.name}Name`].message} />
              ) : (
                ''
              )}
            </div>
          </div>

          <div className={`${className} ${classNameMarginTop}`}>
            <div className='form-group mt-2'>
              <Label
                label='Last Name'
                name={`${item.name}LastName`}
                errors={errors}
                required={item.required}
              />

              <CustomIcon
                fieldsValidated={fieldsValidated}
                fieldError={errors[`${item.name}LastName`]}
                required={item.required}
              />

              <InputText name={`${item.name}LastName`} />

              {errors[`${item.name}LastName`] ? (
                <Span text={errors[`${item.name}LastName`].message} />
              ) : (
                ''
              )}
            </div>
          </div>

          <div className={`${className} ${classNameMarginTop}`}>
            <div className='form-group mt-2'>
              <Label
                label='Email'
                name={`${item.name}Email`}
                errors={errors}
                required={item.required}
              />

              <CustomIcon
                fieldsValidated={fieldsValidated}
                fieldError={errors[`${item.name}Email`]}
                required={item.required}
              />

              <InputEmail name={`${item.name}Email`} />

              {errors[`${item.name}Email`] ? (
                <Span text={errors[`${item.name}Email`].message} />
              ) : (
                ''
              )}

              {!watch(`${item.name}EmailEducationalInstitution`) &&
              watch(`${item.name}Email`) ? (
                <div>
                  <Label
                    className='mt-3'
                    label='Institution / organisation'
                    name={`${item.name}Organisation`}
                    errors={errors}
                    required={item.required}
                  />
                  <InputText name={`${item.name}Organisation`} />
                  {errors[`${item.name}Organisation`] ? (
                    <Span text={errors[`${item.name}Organisation`].message} />
                  ) : (
                    ''
                  )}
                </div>
              ) : null}
            </div>
          </div>

          <div className={`${className} ${classNameMarginTop}`}>
            <div className='form-group mt-2'>
              <Label
                label='Country'
                name={`${item.name}CountryId`}
                errors={errors}
                required={item.required}
              />

              <CustomIcon
                fieldsValidated={fieldsValidated}
                fieldError={errors[`${item.name}CountryId`]}
                required={item.required}
              />

              <CountrySelector name={`${item.name}CountryId`} />

              {errors[`${item.name}CountryId`] ? (
                <Span text={errors[`${item.name}CountryId`].message} />
              ) : (
                ''
              )}
            </div>
          </div>

          <div className={`col-lg-12 ${classNameMarginTop}`}>
            <div className='form-group mt-3 mb-xl-5'>
              <Label
                label='Academy membership/s (or equivalent)'
                name={`${item.name}Memberships`}
                errors={errors}
                required={item.required}
              />

              <CustomIcon
                fieldsValidated={fieldsValidated}
                fieldError={errors[`${item.name}Memberships`]}
                required={item.required}
              />

              <Markdown
                key='elements-description-markdown-1'
                className='col-xl-12 col-lg-12'
                text='eg., FAA FTSE FASc FMRS'
              />

              <TextArea
                name={`${item.name}Memberships`}
                className={className}
              />

              {errors[`${item.name}Memberships`] ? (
                <Span text={errors[`${item.name}Memberships`].message} />
              ) : (
                ''
              )}
            </div>
          </div>
        </>
      ) : (
        ''
      )}

      {parseInt(watch(item.name)) === 1 ? (
        <div className={`${className} ${classNameMarginTop} mb-lg-5`}>
          <div className='form-group'>
            <Label
              className='mb-3'
              label='Select existing AAS fellow'
              name={`${item.name}FellowId`}
              errors={errors}
              required={item.required}
            />

            <CustomIcon
              fieldsValidated={fieldsValidated}
              fieldError={errors[`${item.name}FellowId`]}
              required={item.required}
            />

            <Select options='fellows' name={`${item.name}FellowId`} />

            {showInactiveWarningFellow && selectedFellow && (
              <div className='alert alert-warning mt-3' role='alert'>
                <i className='bi bi-exclamation-triangle-fill me-2' />
                <strong>Warning:</strong> {selectedFellow.attributes.name}{' '}
                {selectedFellow.attributes.lastName} is deceased.
              </div>
            )}

            {errors[`${item.name}FellowId`] ? (
              <Span text={errors[`${item.name}FellowId`].message} />
            ) : (
              ''
            )}
          </div>

          <Markdown
            className='col-xl-12 col-lg-12 mt-3'
            text={item.description}
          />
        </div>
      ) : (
        ''
      )}
    </React.Fragment>
  );
};

NominationReferee.propTypes = {
  item: PropTypes.object,
  className: PropTypes.string,
  classNameMarginTop: PropTypes.string,
  fieldsValidated: PropTypes.bool,
};

export default NominationReferee;
